import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { Box, Typography, CircularProgress } from '@mui/material';
import { ArrowBack, ArrowForwardIos } from '@mui/icons-material';
import NavBar from '../../components/NavBar';
import { colors, typography, radius } from '../../theme';

// ─── localStorage helpers ─────────────────────────────────────────────────────
const STORAGE_KEY = 'guntingjer_queue_tickets';

export const saveTicketToStorage = (ticket) => {
    try {
        const existing = getStoredTickets();
        const filtered = existing.filter(t => t.bookingId !== ticket.bookingId);
        const updated  = [ticket, ...filtered].slice(0, 10);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    } catch (e) { console.error('Failed to save ticket:', e); }
};

export const getStoredTickets = () => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]'); }
    catch { return []; }
};

export const removeTicketFromStorage = (bookingId) => {
    try {
        const updated = getStoredTickets().filter(t => t.bookingId !== bookingId);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    } catch (e) { console.error(e); }
};

// ─── TicketRow ────────────────────────────────────────────────────────────────
const TicketRow = ({ ticket, liveStatus, onOpen, onRemove }) => {
    const isActive = liveStatus === 'waiting' || liveStatus === 'serving';
    const isDone   = liveStatus === 'completed';

    const statusColor = liveStatus === 'serving'  ? colors.success
                      : liveStatus === 'completed' ? colors.info
                      : liveStatus === 'cancelled' ? colors.error
                      : colors.primary;

    const statusIcon = liveStatus === 'serving'  ? '✂️'
                     : liveStatus === 'completed' ? '✅'
                     : liveStatus === 'cancelled' ? '❌'
                     : '🕐';

    return (
        <Box sx={{ bgcolor: colors.bgCard, border: `1px solid ${isActive ? `${colors.primary}44` : colors.border}`, borderRadius: radius.lg, overflow:'hidden', position:'relative' }}>
            {isActive && <Box sx={{ position:'absolute', top:0, left:0, right:0, height:2, bgcolor: colors.primary }} />}

            <Box sx={{ p:2 }}>
                <Box sx={{ display:'flex', alignItems:'center', gap:1.5 }}>

                    {/* Queue number circle */}
                    <Box sx={{ width:48, height:48, borderRadius:'50%', flexShrink:0, border:`2px solid ${statusColor}44`, bgcolor: colors.bg, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center' }}>
                        <Typography sx={{ fontSize:9, color: colors.textFaintest, lineHeight:1 }}>No.</Typography>
                        <Typography sx={{ fontFamily: typography.poppins, fontSize:18, fontWeight:700, color: statusColor, lineHeight:1 }}>
                            {ticket.queueNumber}
                        </Typography>
                    </Box>

                    {/* Info */}
                    <Box sx={{ flex:1, minWidth:0 }}>
                        <Typography sx={{ fontSize:14, fontWeight:600, color: colors.textPrimary, lineHeight:1.2 }} noWrap>{ticket.shopName}</Typography>
                        <Typography sx={{ fontSize:12, color: colors.textMuted }}>{ticket.barberName} · {ticket.slotLabel}</Typography>
                        <Box sx={{ display:'inline-flex', alignItems:'center', gap:0.4, mt:0.4 }}>
                            <Typography sx={{ fontSize:11 }}>{statusIcon}</Typography>
                            <Typography sx={{ fontSize:11, color: statusColor, fontWeight:600 }}>
                                {liveStatus === 'serving'  ? "It's your turn!"
                               : liveStatus === 'completed' ? 'Done'
                               : liveStatus === 'cancelled' ? 'Cancelled'
                               : 'Waiting'}
                            </Typography>
                        </Box>
                    </Box>

                    <Box onClick={() => onOpen(ticket)} sx={{ display:'flex', alignItems:'center', cursor:'pointer', p:0.5 }}>
                        <ArrowForwardIos sx={{ fontSize:14, color: colors.primary }} />
                    </Box>
                </Box>

                {isActive && (
                    <Box onClick={() => onOpen(ticket)} sx={{ mt:1.5, py:0.8, borderRadius:1.5, bgcolor: colors.primaryDim, border:`1px solid ${colors.primaryBorder}`, textAlign:'center', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:0.5, '&:hover':{ bgcolor:`${colors.primary}30` } }}>
                        <Typography sx={{ fontSize:12, color: colors.primary, fontWeight:600 }}>📋 View Live Queue Position</Typography>
                        <ArrowForwardIos sx={{ fontSize:10, color: colors.primary }} />
                    </Box>
                )}

                {(isDone || liveStatus === 'cancelled') && (
                    <Box onClick={() => onRemove(ticket.bookingId)} sx={{ mt:1, textAlign:'center', cursor:'pointer' }}>
                        <Typography sx={{ fontSize:11, color: colors.textFaintest, '&:hover':{ color: colors.error } }}>
                            Remove from list
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

// ─── MyQueue page ─────────────────────────────────────────────────────────────
const MyQueue = () => {
    const navigate = useNavigate();
    const [tickets,      setTickets]      = useState([]);
    const [liveStatuses, setLiveStatuses] = useState({});
    const [loading,      setLoading]      = useState(true);

    useEffect(() => {
        const stored = getStoredTickets();
        setTickets(stored);
        setLoading(false);
        const fetchStatuses = async () => {
            const statuses = {};
            await Promise.all(stored.map(async (t) => {
                try {
                    const snap = await getDoc(doc(db, 'bookings', t.bookingId));
                    if (snap.exists()) statuses[t.bookingId] = snap.data().status;
                } catch { statuses[t.bookingId] = 'unknown'; }
            }));
            setLiveStatuses(statuses);
        };
        if (stored.length) fetchStatuses();
    }, []);

    const handleOpen   = (ticket) => navigate(`/queue/${ticket.shopId}/${ticket.bookingId}`);
    const handleRemove = (bookingId) => {
        removeTicketFromStorage(bookingId);
        setTickets(prev => prev.filter(t => t.bookingId !== bookingId));
    };

    return (
        <Box sx={{ bgcolor: colors.bg, minHeight:'100vh', color: colors.textPrimary, pb:8 }}>
            <Box sx={{ px:3, mt:3, display:'flex', flexDirection:'column', gap:2 }}>
                {loading ? (
                    <CircularProgress sx={{ color: colors.primary, mx:'auto', mt:4 }} />
                ) : tickets.length === 0 ? (
                    <Box sx={{ textAlign:'center', py:8 }}>
                        <Typography sx={{ fontSize:36, mb:2 }}>🎫</Typography>
                        <Typography sx={{ fontSize:16, color: colors.textFaint }}>No queue tickets yet</Typography>
                        <Typography sx={{ fontSize:13, color: colors.textFaintest, mt:0.5 }}>Scan a shop's QR code to walk in</Typography>
                    </Box>
                ) : tickets.map(ticket => (
                    <TicketRow key={ticket.bookingId} ticket={ticket} liveStatus={liveStatuses[ticket.bookingId]} onOpen={handleOpen} onRemove={handleRemove} />
                ))}
            </Box>
        </Box>
    );
};

export default MyQueue;